import type { multiSelectFieldSchema } from "@/app/directories/checkout/components/intake/IntakeFormTypes";
import { Button } from "@frontend/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@frontend/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { cn } from "@frontend/shared/lib/utils";
import { Check, ChevronDown, X } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import type { z } from "zod";

interface MultiSelectDropdownProps {
  data: { value: string; label: string }[];
  onSelectValue: (datum: {
    value: string;
    label: string;
    [x: string]: any;
  }) => void;
  labelText: string | React.ReactNode;
  promptText: string;
  valueText: string;
  selectedValues: { value: string; label: string }[];
  pdfquestion?: z.infer<typeof multiSelectFieldSchema>;
  classNames?: string;
  pcMode?: boolean;
  disabled?: boolean;
  intakeClassNames?: string;
}

export function MultiSelectDropdownPC({
  data,
  onSelectValue,
  labelText,
  promptText,
  valueText,
  selectedValues,
  pdfquestion,
  classNames,
  pcMode = false,
  disabled,
  intakeClassNames,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "w-full justify-between text-sm font-normal text-left overflow-hidden",
              classNames,
              "hover:bg-medGrey",
            )}
          >
            <div
              className={cn(
                selectedValues.length === 0 && "text-gray-400 text-xs",
              )}
            >
              {promptText}
            </div>

            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn("p-0", classNames, "h-fit")}>
          <Command>
            <CommandList>
              <CommandEmpty>No {valueText} found.</CommandEmpty>
              <CommandGroup
                className={cn(
                  "overflow-y-scroll border-superscriptBlue max-h-[185px] border rounded-2xl bg-white",
                )}
              >
                {data.map((datum) => (
                  <CommandItem
                    key={datum.label}
                    value={datum.label}
                    onSelect={() => onSelectValue(datum)}
                    className={
                      "justify-between rounded-2xl data-[selected=true]:bg-[#00a3ff11] hover:bg-medGrey p-2"
                    }
                  >
                    {datum.label}
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedValues.includes(datum)
                          ? "opacity-100"
                          : "opacity-0",
                        "text-superscriptBlue",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}

// uses native html select element
export function MultiSelectDropdown({
  data,
  onSelectValue,
  labelText,
  promptText,
  valueText,
  selectedValues,
  pdfquestion,
  classNames,
  pcMode = false,
  disabled,
  intakeClassNames,
}: MultiSelectDropdownProps) {
  return (
    <div>
      <label
        htmlFor="multi-select-dropdown"
        className="text-sm font-normal block mb-2"
        data-pdflabel="multiInput-label"
        data-questionid={pdfquestion?.id}
      >
        {labelText}
      </label>

      <div className="relative">
        <select
          id="multi-select-dropdown"
          value=""
          onChange={(e) => {
            const selectedOption = data.find(
              (datum) => datum.value === e.target.value,
            );
            if (selectedOption) {
              onSelectValue(selectedOption);
            }
          }}
          disabled={disabled}
          className={cn(
            "w-full border rounded-md text-sm",
            "appearance-none bg-white",
            intakeClassNames,
            disabled && "opacity-50 cursor-not-allowed",
          )}
          aria-label={promptText}
        >
          <option value="" disabled>
            {promptText}
          </option>
          {data.map((datum) => (
            <option key={datum.value} value={datum.value}>
              {datum.label}
            </option>
          ))}
        </select>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-label="Dropdown arrow"
          >
            <title>Dropdown arrow</title>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {selectedValues.length > 0 && (
        <div className="mt-2 space-y-2">
          {selectedValues.map((value) => (
            <div key={value.value} className="flex items-center py-2">
              <X
                size={16}
                onClick={() => onSelectValue(value)}
                className="mr-2 cursor-pointer"
              />
              <p
                data-pdflabel="multiInput-choice"
                data-questionid={pdfquestion?.id}
                data-choiceindex={data
                  .map((datum) => datum.value)
                  .indexOf(value.value)}
              >
                {value.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
